import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import styled from '@emotion/styled';

import { FlexBoxWrap } from 'styles/layout';
import colors from 'styles/colors';

import Select from './Select';

const StyledChip = styled(Chip)`
  margin-bottom: 4px;
  margin-right: 4px;
`;

const RemainingChip = styled(StyledChip)`
  background-color: ${colors.grayLight};
  font-style: italic;
`;

const StyledPlaceHolder = styled(Box)`
  color: ${colors.placeholder};
`;

const ControlledSelect = ({
  name,
  control,
  defaultValue,
  handleChange,
  placeholder,
  options = [],
  multiple,
  compactDisplay = false,
  ...props
}) => {
  const valueDefault = useMemo(
    () => defaultValue || (multiple ? [] : ''),
    [defaultValue, multiple],
  );

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={valueDefault}
      render={({ field: { value, onChange, ...fieldProps } }) => (
        <Select
          {...fieldProps}
          onChange={(e) => {
            onChange(e);
            if (handleChange) handleChange(e);
          }}
          value={options.length ? value : valueDefault}
          multiple={multiple}
          options={options}
          native={false}
          placeholder={placeholder}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            PaperProps: {
              style: compactDisplay ? { maxHeight: 200 } : {},
            },
          }}
          displayEmpty
          renderValue={() => {
            if ((multiple ? isEmpty(value) : !value) || isEmpty(options)) {
              return <StyledPlaceHolder>{placeholder}</StyledPlaceHolder>;
            }
            if (multiple) {
              const selectedOptions = options.filter((opt) =>
                value.includes(opt.value),
              );
              if (compactDisplay) {
                const maxDisplay = 2;
                const displayedOptions = selectedOptions.slice(0, maxDisplay);
                const remaining =
                  selectedOptions.length - displayedOptions.length;
                return (
                  <FlexBoxWrap>
                    {displayedOptions.map(({ text }) => (
                      <StyledChip key={text} label={text} />
                    ))}
                    {remaining > 0 && (
                      <RemainingChip label={`+${remaining} more`} />
                    )}
                  </FlexBoxWrap>
                );
              }
              return (
                <FlexBoxWrap>
                  {selectedOptions?.map(({ text }) => (
                    <StyledChip key={text} label={text} />
                  ))}
                </FlexBoxWrap>
              );
            }
            return options?.find((opt) => opt.value === value)?.text;
          }}
          {...props}
        />
      )}
    />
  );
};

ControlledSelect.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  handleChange: PropTypes.func,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  compactDisplay: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string,
    }),
  ).isRequired,
};

export default ControlledSelect;
